import { Glyph } from '@/components/svgs/Glyph'
import { colors } from '@/constants/colors'
import { Pressable } from '@/ui/core/pressable'
import { HStack } from '@/ui/core/stack'
import { cn } from '@/ui/utils'
import type { ReactNode } from 'react'
import { View } from 'react-native'

/**
 * Something offered to be picked: white on the note it sits on, with its mark at the left where
 * every row in this app carries its glyph. Picked, it fills black and the mark inverts, which is
 * how the brand says a thing is chosen.
 *
 * The mark is round when only one answer can be given and square when several can, so its shape
 * says what a tap will do before the tap is made.
 */
export const choiceFrame = (checked: boolean) =>
	cn(
		'rounded-brand border px-4 py-3 min-h-14 justify-center',
		checked ? 'bg-ink border-ink' : 'bg-white border-edge'
	)

export const ChoiceMark = ({
	checked,
	multi,
	onInk = false,
}: {
	checked: boolean
	multi: boolean
	/** The mark is standing on a filled row, so it is cut out of it rather than drawn on it. */
	onInk?: boolean
}) => {
	const shape = multi ? 'rounded-md' : 'rounded-full'

	if (!checked) {
		return (
			<View
				className={cn('h-6 w-6 border-2', shape)}
				style={{ borderColor: onInk ? colors.white : colors.edge }}
			/>
		)
	}

	return (
		<View
			className={cn('h-6 w-6 items-center justify-center', shape)}
			style={{ backgroundColor: onInk ? colors.white : colors.ink }}
		>
			<Glyph name="check" size={13} color={onInk ? colors.ink : colors.white} />
		</View>
	)
}

export const ChoiceRow = ({
	checked,
	multi,
	onPress,
	accessibilityLabel,
	field = false,
	children,
}: {
	checked: boolean
	multi: boolean
	onPress: () => void
	accessibilityLabel?: string
	/**
	 * The row is a field's frame rather than a control of its own: the thing inside it is what is
	 * operated, so the row steps out of the way of anything reading the screen. Announced as a
	 * checkbox it would both name itself wrongly and hide the field it contains, leaving a control
	 * that says it ticks and in fact opens a keyboard.
	 */
	field?: boolean
	children: ReactNode
}) => (
	<Pressable
		onPress={onPress}
		// One answer or several: a mutually exclusive choice is a radio, whatever its mark looks
		// like, and the app should say which it is rather than call everything a checkbox.
		accessibilityRole={field ? undefined : multi ? 'checkbox' : 'radio'}
		accessibilityState={field ? undefined : { checked }}
		accessibilityLabel={field ? undefined : accessibilityLabel}
		accessible={!field}
		className={choiceFrame(checked)}
	>
		<HStack className="items-center" gap={12}>
			<View importantForAccessibility="no-hide-descendants" accessibilityElementsHidden>
				<ChoiceMark checked={checked} multi={multi} onInk={checked} />
			</View>
			{children}
		</HStack>
	</Pressable>
)
